import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Search, FileText, Users, Rocket, Shield, Database, Lock, CheckCircle, Target, TrendingUp } from 'lucide-react';

const Approach = () => {
  const approachSteps = [
    {
      step: 1,
      title: 'Discover',
      description: 'We assess organisational context, AI use cases, risk exposure, maturity, regulatory obligations, and existing governance structures across AI, data, cybersecurity, and enterprise risk.',
      icon: Search
    },
    {
      step: 2,
      title: 'Plan',
      description: 'We design fit-for-purpose AI ethics and governance frameworks, policies, maturity models, or guardrails aligned with international standards, regulatory expectations, and organisational objectives.',
      icon: FileText
    },
    {
      step: 3,
      title: 'Unify',
      description: 'We integrate AI governance into existing organisational systems, culture, and decision-making processes, ensuring alignment across technology, legal, risk, security, and leadership functions.',
      icon: Users
    },
    {
      step: 4,
      title: 'Deliver Actionable Outcomes',
      description: 'We translate principles into operational guidance, governance mechanisms, and decision tools that organisations can confidently implement, monitor, and evolve over time.',
      icon: Rocket
    }
  ];

  const governanceAreas = [
    {
      title: 'Data Quality & Privacy',
      description: 'Data quality, provenance, privacy, and lifecycle management',
      icon: Database
    },
    {
      title: 'Cybersecurity & Resilience',
      description: 'Cybersecurity controls and resilience for AI systems',
      icon: Lock
    },
    {
      title: 'Accountability & Auditability',
      description: 'Accountability, traceability, and auditability',
      icon: CheckCircle
    },
    {
      title: 'Risk Management',
      description: 'Organisational risk management and internal controls',
      icon: Shield
    },
    {
      title: 'Governance Alignment',
      description: 'Alignment with existing governance, compliance, and assurance structures',
      icon: Target
    }
  ];

  const outcomes = [
    'Adopt AI responsibly and with confidence',
    'Strengthen trust with regulators, customers, and communities',
    'Augment human capabilities rather than replace them',
    'Embed ethical and sustainable practices into organisational culture',
    'Prepare for emerging AI risks and future technologies'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight mb-8 animate-in fade-in slide-in-from-bottom-8 duration-1000">
              Our
              <span className="block font-normal mt-2">Approach</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed font-medium">
              A Comprehensive, Standards-Aligned Pathway to Trustworthy and Sustainable AI
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI applies a comprehensive, systems-level approach to the design and implementation of AI ethics, governance, and assurance. Whether supporting the development of national AI strategies, public sector AI governance frameworks, organisational AI maturity models, AI safety guardrails, AI impact assessments, AI policies, or responsible AI operating models, our approach is grounded in a deep understanding of the global AI governance ecosystem and its interdependencies.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We recognise that effective AI governance cannot be addressed in isolation. It must be embedded within broader frameworks of data governance, cybersecurity, organisational governance, risk management, and audit, and aligned with international standards, regulatory obligations, and societal expectations.
            </p>
          </div>
        </div>
      </section>

      {/* Standards & Policy Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl">
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-8">
              Standards- and Policy-Informed by Design
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our work is informed by sustained engagement with global standards, regulatory frameworks, and voluntary guidance developed by leading international bodies, including the OECD, the IEEE Standards Association, ISO, and the IET. We also draw on best-practice frameworks and normative guidance developed by UNESCO, the European Union, and the National Institute of Standards and Technology.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This enables us to design AI governance approaches that are risk-based, proportionate, and context-aware, while remaining aligned with emerging regulatory regimes such as the EU AI Act and international AI policy principles.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-12">
            {['OECD', 'IEEE', 'ISO', 'IET', 'UNESCO', 'NIST'].map((org) => (
              <Card key={org} className="hover:shadow-lg transition-all duration-300">
                <CardContent className="p-4 text-center">
                  <p className="text-lg font-light">{org}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integration Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-8">
            Integrated with Data Governance, Cybersecurity, and Risk
          </h2>
          
          <div className="max-w-4xl mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The Centre for Sustainable AI fully integrates AI ethics and governance with foundational data governance and cybersecurity requirements, recognising these as critical enablers of trustworthy AI. Our approach is informed by regulatory and standards frameworks such as General Data Protection Regulation and ISO/IEC 27001, as well as established practices in enterprise risk management, assurance, and audit.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We ensure that AI governance frameworks account for:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {governanceAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-normal mb-2">{area.title}</h3>
                        <p className="text-gray-600 text-sm">{area.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 max-w-4xl">
            <p className="text-lg text-gray-700 leading-relaxed italic">
              This integrated view allows organisations to avoid fragmented or duplicative governance and instead establish coherent, defensible, and scalable AI governance architectures.
            </p>
          </div>
        </div>
      </section>

      {/* Approach Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-4 text-center">
            Discover. Plan. Unify. Deliver.
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12">
            Our approach follows a clear and practical pathway
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {approachSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card
                  key={step.step}
                  className="hover:shadow-lg hover:-translate-y-2 transition-all duration-300 animate-in fade-in slide-in-from-bottom-4"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <CardContent className="p-8">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 w-14 h-14 bg-black text-white rounded-lg flex items-center justify-center mr-6">
                        <IconComponent className="w-7 h-7" />
                      </div>
                      <div>
                        <div className="flex items-center gap-3 mb-3">
                          <span className="text-sm text-gray-500 font-medium">Step {step.step}</span>
                        </div>
                        <h3 className="text-2xl font-normal mb-4">{step.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{step.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Long-Term Value Section */}
      <section className="py-24 bg-black text-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center">
                  <TrendingUp className="w-7 h-7 text-black" />
                </div>
              </div>
              <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6">
                Long-Term Value and Sustainable Impact
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                Our approach is designed to deliver actionable insights and durable outcomes, enabling organisations to build resilient, future-ready AI capabilities.
              </p>
            </div>

            <div className="space-y-4">
              {outcomes.map((outcome, index) => (
                <div key={index} className="flex items-start gap-4 p-4 bg-white/5 rounded-lg">
                  <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="w-4 h-4 text-black" />
                  </div>
                  <p className="text-gray-200">{outcome}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16 pt-12 border-t border-gray-800 max-w-4xl">
            <p className="text-lg text-gray-300 leading-relaxed">
              By combining global policy insight, standards expertise, and practical implementation experience, the Centre for Sustainable AI helps organisations move beyond compliance towards resilient, future-ready, and sustainable AI adoption.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Approach;
